import React, { useEffect, useMemo, useState } from 'react';

const normalizeKey = (field, index) =>
  field?.field_key || field?.name || field?.label || `field_${index + 1}`;

const fieldLabel = (field, index) =>
  field?.label || field?.question || field?.field_key || `Field ${index + 1}`;

const DynamicNaukriFieldsModal = ({ isOpen, request, isDark = false, onSubmit, onClose }) => {
  const fields = useMemo(() => request?.fields || [], [request]);
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isOpen) return;
    const initialValues = {};
    fields.forEach((field, index) => {
      const key = normalizeKey(field, index);
      initialValues[key] = field?.value || '';
    });
    setValues(initialValues);
    setErrors({});
  }, [isOpen, fields]);

  if (!isOpen || !request) return null;

  const updateValue = (key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleSubmit = () => {
    const nextErrors = {};
    fields.forEach((field, index) => {
      const key = normalizeKey(field, index);
      const required = field?.required !== false;
      if (required && !String(values[key] || '').trim()) {
        nextErrors[key] = 'This field is required.';
      }
    });
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    onSubmit?.(values);
  };

  const renderField = (field, index) => {
    const key = normalizeKey(field, index);
    const label = fieldLabel(field, index);
    const type = String(field?.type || '').toLowerCase();
    const options = Array.isArray(field?.options) ? field.options.filter(Boolean) : [];
    const commonClass = `w-full rounded-lg border px-3 py-2 text-sm outline-none transition ${
      isDark
        ? 'border-gray-700 bg-gray-900 text-gray-100 focus:border-violet-500'
        : 'border-gray-200 bg-white text-gray-900 focus:border-violet-500'
    }`;

    return (
      <div key={`${key}-${index}`} className="space-y-2">
        <div className="flex items-start justify-between gap-3">
          <label className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
            {label}
          </label>
          {field?.required === false && (
            <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Optional</span>
          )}
        </div>

        {field?.question && field.question !== label && (
          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{field.question}</p>
        )}

        {options.length > 0 ? (
          <select
            className={commonClass}
            value={values[key] || ''}
            onChange={(event) => updateValue(key, event.target.value)}
          >
            <option value="">Select an option</option>
            {options.map((option) => (
              <option key={String(option)} value={String(option)}>
                {String(option)}
              </option>
            ))}
          </select>
        ) : type === 'textarea' || String(label).length > 80 ? (
          <textarea
            className={`${commonClass} min-h-[92px] resize-y`}
            value={values[key] || ''}
            onChange={(event) => updateValue(key, event.target.value)}
            placeholder="Enter value"
          />
        ) : (
          <input
            className={commonClass}
            value={values[key] || ''}
            onChange={(event) => updateValue(key, event.target.value)}
            placeholder="Enter value"
          />
        )}

        {errors[key] && <p className="text-xs font-medium text-red-500">{errors[key]}</p>}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/45 px-4">
      <div
        className={`w-full max-w-3xl rounded-xl shadow-2xl ${
          isDark ? 'bg-gray-950 text-gray-100' : 'bg-white text-gray-900'
        }`}
      >
        <div className={`border-b px-6 py-4 ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold">Naukri needs more details</h3>
              <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {request.stepTitle || 'Current form step'}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className={`flex h-8 w-8 items-center justify-center rounded-lg text-xl leading-none transition ${
                isDark
                  ? 'text-gray-400 hover:bg-gray-800 hover:text-gray-100'
                  : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
              }`}
            >
              ×
            </button>
          </div>
          {request.prompt && (
            <p className={`mt-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {request.prompt}
            </p>
          )}
        </div>

        <div className="max-h-[65vh] space-y-5 overflow-y-auto px-6 py-5">
          {fields.length > 0 ? (
            fields.map(renderField)
          ) : (
            <textarea
              className={`min-h-[140px] w-full rounded-lg border px-3 py-2 text-sm outline-none ${
                isDark ? 'border-gray-700 bg-gray-900 text-gray-100' : 'border-gray-200 bg-white text-gray-900'
              }`}
              value={values.response || ''}
              onChange={(event) => updateValue('response', event.target.value)}
              placeholder="Enter the requested Naukri details"
            />
          )}
        </div>

        <div className={`flex justify-end gap-3 border-t px-6 py-4 ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
          <button
            type="button"
            onClick={handleSubmit}
            className="rounded-lg bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-700"
          >
            Submit Details
          </button>
        </div>
      </div>
    </div>
  );
};

export default DynamicNaukriFieldsModal;
